import React, { useEffect } from 'react';
import {
  Title,
  Text,
  Label,
  Select,
  Option,
  Switch,
  Input,
  MessageStrip,
  Button,
  Icon,
  ObjectStatus,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function SecurityConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const securitySettings = systemSettings?.SECURITY;
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    console.log('SecurityConfig - Current Values:', { formData, securitySettings });
  }, [formData, securitySettings]);

  if (!formData || !securitySettings) {
    return <div>Error: Faltan datos necesarios para la configuración de seguridad</div>;
  }

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const prepareDataForSave = () => ({
    ...securitySettings,
    auditLog:           { ...securitySettings.auditLog,           default: formData.auditLog },
    loginAttempts:      { ...securitySettings.loginAttempts,      default: formData.loginAttempts },
    twoFactorAuth:      { ...securitySettings.twoFactorAuth,      default: formData.twoFactorAuth },
    passwordPolicy:     { ...securitySettings.passwordPolicy,     default: formData.passwordPolicy },
    sessionTimeout:     { ...securitySettings.sessionTimeout,     default: formData.sessionTimeout },
    emailNotifications: { ...securitySettings.emailNotifications, default: formData.emailNotifications },
  });

  const handleSave = () => onSave(prepareDataForSave());

  const hasChanges = () =>
    formData.auditLog           !== securitySettings.auditLog.default           ||
    formData.loginAttempts      !== securitySettings.loginAttempts.default      ||
    formData.twoFactorAuth      !== securitySettings.twoFactorAuth.default      ||
    formData.passwordPolicy     !== securitySettings.passwordPolicy.default     ||
    formData.sessionTimeout     !== securitySettings.sessionTimeout.default     ||
    formData.emailNotifications !== securitySettings.emailNotifications.default;

  const getSecurityStatus = () => [
    {
      item: autoT('Política de contraseñas'),
      status: { low: autoT('Básica'), medium: autoT('Media'), high: autoT('Alta'), custom: autoT('Personalizada') }[formData.passwordPolicy] || autoT('Media'),
      state: formData.passwordPolicy === 'high' ? 'Positive' : 'Critical',
    },
    {
      item: autoT('Autenticación 2FA'),
      status: formData.twoFactorAuth ? autoT('Activada') : autoT('Desactivada'),
      state: formData.twoFactorAuth ? 'Positive' : 'Negative',
    },
    {
      item: autoT('Registro de auditoría'),
      status: formData.auditLog ? autoT('Activo') : autoT('Inactivo'),
      state: formData.auditLog ? 'Positive' : 'Negative',
    },
  ];

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">{autoT('Configuración de Seguridad')}</Title>
          <Text style={CONFIG_STYLES.labelHint}>{autoT('Protege tu sistema con políticas de seguridad avanzadas')}</Text>
        </div>
        <Button
          design="Emphasized"
          onClick={handleSave}
          disabled={!hasChanges() || isLoading}
          icon="save"
        >
          {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
        </Button>
      </div>

      {hasChanges() && (
        <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
          {autoT('Hay cambios pendientes de guardar')}
        </MessageStrip>
      )}

      {/* Políticas */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Políticas de Acceso')}</Title>
        </div>
        <div style={CONFIG_STYLES.formGrid}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Política de Contraseñas')}</Label>
            <Select
              value={formData.passwordPolicy}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('passwordPolicy', e.detail.selectedOption.value)}
            >
              {securitySettings.passwordPolicy.options.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Tiempo de Sesión (minutos)')}</Label>
            <Input
              type="Number"
              value={formData.sessionTimeout}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('sessionTimeout', e.target.value)}
              min={securitySettings.sessionTimeout.min}
              max={securitySettings.sessionTimeout.max}
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Intentos de Login Máximos')}</Label>
            <Select
              value={formData.loginAttempts}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('loginAttempts', e.detail.selectedOption.value)}
            >
              {securitySettings.loginAttempts.options.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {/* Switches */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Opciones de Seguridad')}</Title>
        </div>

        <div style={CONFIG_STYLES.switchRow}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Autenticación de Dos Factores')}</Label>
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Añade una capa extra de seguridad')}</Text>
            {formData.twoFactorAuth && (
              <Text style={CONFIG_STYLES.labelHint}>
                {autoT('Métodos disponibles:')} {securitySettings.twoFactorAuth.methods.join(', ')}
              </Text>
            )}
          </div>
          <Switch
            checked={formData.twoFactorAuth}
            onChange={(e) => handleInputChange('twoFactorAuth', e.target.checked)}
          />
        </div>

        <div style={CONFIG_STYLES.switchRow}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Registro de Auditoría')}</Label>
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Registrar actividades del sistema')}</Text>
          </div>
          <Switch
            checked={formData.auditLog}
            onChange={(e) => handleInputChange('auditLog', e.target.checked)}
          />
        </div>

        <div style={CONFIG_STYLES.switchRowLast}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Alertas por Email')}</Label>
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Notificar eventos de seguridad')}</Text>
          </div>
          <Switch
            checked={formData.emailNotifications}
            onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
          />
        </div>
      </div>

      {/* Estado de Seguridad */}
      <div style={CONFIG_STYLES.infoBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="shield" style={{ color: 'var(--sapHighlightColor)' }} />
          <Title level="H5">{autoT('Estado de Seguridad')}</Title>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {getSecurityStatus().map((item, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <ObjectStatus state={item.state} showDefaultIcon>
                {item.status}
              </ObjectStatus>
              <Text>{item.item}</Text>
            </div>
          ))}
        </div>
      </div>

      {/* Recomendaciones */}
      <div style={CONFIG_STYLES.warningBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="alert" style={{ color: 'var(--sapCriticalTextColor)' }} />
          <Title level="H5">{autoT('Recomendaciones de Seguridad')}</Title>
        </div>
        <ul style={{ color: 'var(--sapContent_LabelColor)', margin: 0, paddingLeft: '1.25rem', lineHeight: '1.8' }}>
          <li><Text>{autoT('Habilita la autenticación de dos factores para mayor seguridad')}</Text></li>
          <li><Text>{autoT('Revisa regularmente los registros de auditoría')}</Text></li>
          <li><Text>{autoT('Mantén una política de contraseñas fuerte')}</Text></li>
        </ul>
      </div>
    </div>
  );
}

export default SecurityConfig;
